import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { Instant, LocalDate, LocalDateTime } from "@js-joda/core";
import { UseQueryResult } from "@tanstack/react-query";
import assertNever from "assert-never";
import firebase from "firebase/app";
import ons from "onsenui";
import * as React from "react";
import * as Ons from "react-onsenui";
import { API } from "../API";
import { AppBackgroundManager } from "../AppBackgroundManager";
import { AgencyRequestActivity } from "../components/AgencyRequestScreen/AgencyRequestActivity";
import { APINotProductionAlert } from "../components/APINotProductionAlert";
import { AppRating } from "../components/AppRating";
import AppUrlListener from "../components/AppUrlListener";
import AvailableTaskActivity from "../components/AvailableTask/AvailableTaskActivity";
import AvailableVisitActivity from "../components/AvailableVisitActivity";
import PaystubDetailsActivity from "../components/CaregiverPaystubs/PaystubDetails/PaystubDetailsActivity";
import PaystubListActivity from "../components/CaregiverPaystubs/PaystubList/PaystubListActivity";
import CarebackPointsActivity from "../components/CaregiverPoints/CarebackPointsActivity";
import CareMomentsActivity from "../components/CareMoments/CareMomentsActivity";
import CareMomentsExplainedActivity from "../components/CareMomentsExplained/CareMomentsExplainedActivity";
import CareMomentsIntroActivity from "../components/CareMomentsIntro/CareMomentsIntroActivity";
import CaseSettingsActivity from "../components/CaseSettings/CaseSettingsActivity";
import { getAnomalies, getAnomaliesArray } from "../components/CaseSettings/utils/getAnomalies";
import CaseSettingsV2Activity from "../components/CaseSettingsV2/CaseSettingsV2Activity";
import ChatRoomActivity from "../components/Chat/ChatRoom/ChatRoomActivity";
import ComplianceFormActivity from "../components/Compliance/ComplianceForm/ComplianceFormActivity";
import ComplianceI9Activity from "../components/Compliance/ComplianceForm/ComplianceI9Activity";
import ComplianceDocumentActivity from "../components/Compliance/MyMedflytCompliance/ComplianceDocumentActivity";
import ComplianceItemActivity from "../components/Compliance/MyMedflytCompliance/ComplianceItemActivity";
import CompliantItemsActivity from "../components/Compliance/MyMedflytCompliance/CompliantItemsActivity";
import MyMedflytComplianceActivity from "../components/Compliance/MyMedflytCompliance/MyMedflytComplianceActivity";
import { ComplianceActivity } from "../components/ComplianceActivity";
import ConvertImportedVisitActivity from "../components/ConvertImportedVisitActivity";
import DocumentDownloadActivity from "../components/DocumentDownloadActivity";
import EditBankInformationActivity from "../components/EditBankInformation/EditBankInformationActivity";
import { ErrorBoundary } from "../components/ErrorBoundary";
import FillBankInformationActivity from "../components/FillBankInformation/FillBankInformationActivity";
import { If } from "../components/Generic/If";
import { isDefined } from "../components/Generic/IsDefined";
import ImportantInformationActivity from "../components/ImportantInformation/ImportantInformationActivity";
import InAppNotifications from "../components/InAppNotification/InAppNotifications";
import InformationCenterActivity from "../components/InformationCenterActivity";
import InstaPayActivity from "../components/InstaPay/InstaPayActivity";
import InstaPayHistoryActivity from "../components/InstaPayHistory/InstaPayHistoryActivity";
import LogRocketAnalytics from "../components/LogRocketAnalytics";
import MainActivityController from "../components/Main/MainActivityController";
import { MainScreenTab, MainScreenTabNavigation } from "../components/Main/MainTypes";
import { MedflytPassportCategoryScreen } from "../components/MedflytPassportCategoryScreen";
import MedflytPassportActivity from "../components/MedflytPassportDocuments/MedflytPassportActivity";
import { MedflytPassportDocumentsScreen } from "../components/MedflytPassportDocumentsScreen";
import MyAgenciesActivity from "../components/MyAgencies/MyAgenciesActivity";
import MyStaffingPreferencesActivity from "../components/MyStaffingPreferences.tsx/MyStaffingPreferencesActivity";
import OnboardingActivity from "../components/Onboarding/OnboardingActivity";
import OnboardingSectionActivity from "../components/OnboardingSection/OnboardingSectionActivity";
import OnboardingVisitActivity from "../components/OnboardingVisitActivity";
import OtherAgenciesChatActivity from "../components/OtherAgenciesChat/OtherAgenciesChatActivity";
import PatientActivity from "../components/Patient/PatientActivity";
import PatientScheduleActivity from "../components/PatientScheduleActivity";
import PatientTaskInstanceActivity from "../components/PatientTaskInstanceActivity";
import PatientTaskInstanceDocumentActivity from "../components/PatientTaskInstanceDocument/PatientTaskInstanceDocumentActivity";
import PatientVisitsAndTasksActivity from "../components/PatientVisitsAndTasks/PatientVisitsAndTasksActivity";
import { PromoPopoverScreen } from "../components/PromoPopoverScreen";
import RaffleActivity from "../components/Raffle/RaffleActivity";
import { RecruitmentMainScreen } from "../components/RecruitmentMainScreen";
import RaffleInfoActivity from "../components/ReffleInfo/RaffleInfoActivity";
import { RescheduleScreen } from "../components/RescheduleScreen";
import SelectInstaPayVisitsActivity from "../components/SelectInstaPayVisits/SelectInstaPayVisitsActivity";
import SettingsGeneralScreen from "../components/SettingsGeneralScreen";
import SettingsHelpActivity from "../components/SettingsHelpActivity";
import SettingsSocialScreen from "../components/SettingsSocialScreen";
import StoreRedirect from "../components/StoreRedirect";
import TimesheetActivity from "../components/Timesheet/TimesheetActivity";
import ToastsContainer from "../components/Toaster/ToastsContainer";
import TrainingCenterActivity from "../components/TrainingCenter/TrainingCenterActivity";
import { TrainingCenterLanguagePreference } from "../components/TrainingCenter/TrainingCenterLanguageLookup";
import TrainingCenterBundleActivity from "../components/TrainingCenterBundle/TrainingCenterBundleActivity";
import { UploadedFileViewerScreen } from "../components/UploadedFileViewerScreen";
import VirtualIdCardActivity from "../components/VirtualIDCard/VirtualIdCardActivity";
import VisitInstanceActivity from "../components/VisitInstanceActivity";
import VisitPendingConfirmationActivity from "../components/VisitPendingConfirmation/VisitPendingConfirmationActivity";
import WithCaregiver from "../components/WithCaregiver";
import { ConnectionStatusOverlay } from "../ConnectionStatusOverlay";
import { MedflytCovidActivity } from "../CovidModalActivity";
import FirebaseAnalytics from "../FirebaseAnalytics";
import { isLoading, Loading, loading } from "../Loading";
import {
  TRAINING_CENTER_PASSTHROUGH_BUNDLE_DUE_DATE,
  TRAINING_CENTER_PASSTHROUGH_BUNDLE_ITEM,
} from "../LocalStore";
import {
  CaregiverComplianceItemMobile,
  CaregiverComplianceReferenceDocumentData,
  CaregiverUploadDocumentMobile,
  ComplianceStatus,
  CoronaReportAllQuestionsResponse,
  CoronaReportSubmitParams,
  FcmPushNotification,
  HRApplicationForm,
  HRSection,
  OrientationLanguages,
  PassportDocumentCategory,
  PromoPopover,
  TrainingCenterDisplayLanguage,
  TrainingCenterTranslated,
} from "../messages";
import {
  AgencyId,
  AgencyMemberId,
  CaregiverDocumentTypeId,
  ChatRoomId,
  CommCenterTicketId,
  ImportedVisitId,
  OnboardingVisitId,
  PatientDocumentTypeId,
  PatientId,
  PatientTaskInstanceId,
  TrainingCenterBundleDueDateId,
  TrainingCenterBundleItemId,
  VisitBroadcastId,
  VisitInstanceId,
} from "../messages/ObjectIds";
import { OnboardingCaregiversNotificationPayloads } from "../messages/OnboardingCaregiversWorker";
import { CaregiverPatientDocument } from "../messages/PatientDocuments";
import { Paystub } from "../messages/Paystub";
import { AgencyDocumentType, PassportDocumentType } from "../messages/UploadedDocuments";
import {
  ClockInOutCallType,
  ResceduleVisitInstanceDetails,
  VisitInstanceClockinClockoutRequestParams,
  VisitInstanceDetails,
} from "../messages/Visit";
import { retryableModalNetworkRequest } from "../NetworkRequest";
import { PinUnlockOverlayManager, PIN_CODE_KEY } from "../PinUnlockOverlayManager";
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from "../storage_utils";
import { AppState } from "../system/AppState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { TrainingCenterCaregiverBundle } from "../system/AppState/AppStateTrainingCenter";
import {
  AppChatState,
  caregiverChatStrategies,
  ChatRoom,
  ChatState,
  ChatSystemStrategies,
} from "../system/AppState/ChatState";
import { delay } from "../system/delay";
import { EventSourceBinding } from "../system/EventSource";
import { getLanguage } from "../translate";
import analyticsUtils from "../utils/analyticsUtils";
import { fmap } from "../utils/fmap";
import { HashSet } from "../utils/HashSet";
import { List } from "../utils/List";
import platformDefaultAnimation from "../utils/platformDefaultAnimation";
import { AddChatActivity } from "./AddChatActivity";
import { CustomOnboardingRedirectionParams, Page } from "./AppPage";
import { ChatActivity } from "./ChatActivity";
import { EditRequestsActivity } from "./EditRequestsActivity";
import { MedFlytChallengeActivity } from "./MedFlytChallengeActivity";
import { NotificationActivity } from "./NotificationActivity";
import { ProfileActivity } from "./ProfileActivity";
import { RecruitmentFormActivity } from "./RecruitmentFormActivity";
import { ReferralCodeActivity } from "./ReferralCodeActivity";
import { RescheduleRequestActivity } from "./RescheduleRequestActivity";
import { ScheduledVisitDocScreenActivity } from "./ScheduledVisitDocScreenActivity";
import SettingsActivity from "./SettingsActivity";
import { SettingsNotificationActivity } from "./SettingsNotificationActivity";
import { TaskPlanOfCareActivity } from "./TaskPlanOfCareActivity";
import { TrainingCenterBundleItemActivity } from "./TrainingCenterBundleItemActivity";
import { InitPlanOfCare, VisitPlanOfCareActivity } from "./VisitPlanOfCareActivity";
import { AddPhysicianActivity } from "../components/PatientPhysician/AddPhysicianActivity";
import PatientTaskInstanceChangeActivity from "../components/PatientTaskInstanceChangeActivity";

interface AppNavigatorProps {
  appState: AppState;
  api: API;
  chatSystemStrategies: ChatSystemStrategies;
  pinCode: string | null;
  isAfterRegistration: boolean;
  onUserLocked: () => void;
  startWithRequestAgencies: boolean;
  onLogout: () => void;
  onAppNeedsUpdate: () => void;
  initialShouldMainScreenUpdate: boolean;
  shouldShowPromotionalPopover: boolean;
}

interface AppNavigatorState {
  showPromotionalPopover: PromoPopover | null | Loading;
  languagePreference: TrainingCenterLanguagePreference;
  allLanguages: List<TrainingCenterDisplayLanguage>;
  dismissedTrainingCenterAlert: boolean;
  defaultTab: MainScreenTab;
}

export type CommCenterTicketCreateParams =
  | CommCenterTicketCreateParams.General
  | CommCenterTicketCreateParams.VisitBroadcast
  | CommCenterTicketCreateParams.AssigedVisit;
export namespace CommCenterTicketCreateParams {
  export interface General {
    type: "General";
  }
  export interface VisitBroadcast {
    type: "VisitBroadcast";
    patientId: PatientId;
    visitBroadcastId: VisitBroadcastId | OnboardingVisitId;
  }
  export interface AssigedVisit {
    type: "AssigedVisit";
    patientId: PatientId;
    visitInstanceId: VisitInstanceId;
  }
}

export class AppNavigator extends React.Component<AppNavigatorProps, AppNavigatorState> {
  constructor(props: AppNavigatorProps) {
    super(props);

    this.state = {
      showPromotionalPopover: loading(),

      languagePreference: {
        preferredLanguage: TrainingCenterDisplayLanguage.wrap("English"),
      },

      allLanguages: List.empty(),

      dismissedTrainingCenterAlert: false,

      defaultTab: "Visits",
    };

    this.initTrainingCenterLanguage();

    this.appBackgroundManager = new AppBackgroundManager(this.props.appState);

    this.pinUnlockOverlayManager = new PinUnlockOverlayManager(
      this.props.pinCode,
      () => {
        this.logout(true);
      },
      () => {
        if (isLoading(this.props.appState.appCaregiverState)) {
          return;
        }
        if (this.props.appState.appCaregiverState.caregiver.phoneNumbers.length === 0) {
          return;
        }

        this.props.api
          .setBadLoginAttempt(
            this.props.appState.appCaregiverState.caregiver.phoneNumbers[0].phoneNumber
          )
          .then(() => {
            return;
          })
          .catch((err: any) => {
            if (err.statusCode !== undefined && err.statusCode === 423) {
              this.props.onUserLocked();
            }
          });
      }
    );

    this.trainingCenterUpdatedBinding = this.props.appState.onTrainingCenterUpdated.register(() => {
      if (!isLoading(this.props.appState.appTrainingCenterState)) {
        const newLanguagesSet = trainingCenterFindAllLanguages(
          this.props.appState.appTrainingCenterState.bundles
        );

        this.setState((prevState) => {
          const updatedLanguagesSet = HashSet.fromCollection(prevState.allLanguages).union(
            newLanguagesSet
          );

          return {
            allLanguages:
              updatedLanguagesSet.size() > prevState.allLanguages.length()
                ? List.from(updatedLanguagesSet.values()).sortBy((x) =>
                    TrainingCenterDisplayLanguage.unwrap(x)
                  )
                : prevState.allLanguages,
          };
        });
      }
    });
  }

  private trainingCenterUpdatedBinding: EventSourceBinding;

  initTrainingCenterLanguage = () => {
    getLanguage().then((deviceLanguage) => {
      let lang: TrainingCenterDisplayLanguage = TrainingCenterDisplayLanguage.wrap("English");

      switch (deviceLanguage) {
        case "en":
          lang = TrainingCenterDisplayLanguage.wrap("English");
          break;
        case "es":
          lang = TrainingCenterDisplayLanguage.wrap("Spanish");
          break;
        case "ru":
          lang = TrainingCenterDisplayLanguage.wrap("Russian");
          break;
        case "zh":
          lang = TrainingCenterDisplayLanguage.wrap("Chinese");
          break;
        case "fr":
          lang = TrainingCenterDisplayLanguage.wrap("French");
          break;
        case "ar":
          lang = TrainingCenterDisplayLanguage.wrap("Arabic");
          break;
        case "ht":
          lang = TrainingCenterDisplayLanguage.wrap("Creole");
          break;
        case "he":
          lang = TrainingCenterDisplayLanguage.wrap("Hebrew");
          break;
        case "kr":
          lang = TrainingCenterDisplayLanguage.wrap("Korean");
          break;
        default:
          lang = TrainingCenterDisplayLanguage.wrap("English");
      }

      if (this.state.languagePreference.preferredLanguage !== lang) {
        this.setState({
          languagePreference: {
            preferredLanguage: lang,
          },
        });
      }
    });
  };

  initFirebaseAnalytics = () => {
    if (Capacitor.getPlatform() !== "web") {
      return;
    }

    try {
      FirebaseAnalytics.initializeFirebase({
        apiKey: "AIzaSyAKwaR3K5U1UwkwPuGdBZm-SEY3_U65DIQ",
        authDomain: "medflytdb-bf399.firebaseapp.com",
        databaseURL: "https://medflytdb-bf399.firebaseio.com",
        projectId: "medflytdb-bf399",
        storageBucket: "medflytdb-bf399.appspot.com",
        messagingSenderId: "1098960494201",
        appId: "1:1098960494201:web:7dc384dcb0485eeecc3ea8",
        measurementId: "G-TM26DJ98R5",
      });
    } catch (e) {
      if (e instanceof Error && /Firebase app already exists/.test(e.message)) {
        return;
      }

      console.error(e);
    }

    if (!isLoading(this.props.appState.appCaregiverState)) {
      FirebaseAnalytics.setUserId({
        userId: `caregiver:${this.props.appState.appCaregiverState.caregiver.id}`,
      });
    }
  };

  resetToMainStack = async () => {
    if (this.navigator === null) {
      return;
    }
    while (this.navigator.routes.length > 1) {
      const animation = this.navigator.routes.length === 2 ? platformDefaultAnimation() : "none";
      this.navigator.popPage({ animation: animation });
      await delay(1);
    }
  };

  showRequestAgencyStack = () => {
    if (this.navigator === null) {
      return;
    }

    this.navigator.resetPageStack([
      {
        type: "Main",
        defaultNavigation: {
          type: "Visits",
        },
      },
      {
        type: "MyAgencies",
        addAgency: false,
        resetOnFinish: false,
        popOnNotWorkingWithAgency: false,
      },
      {
        type: "MyAgencies",
        addAgency: true,
        resetOnFinish: false,
        popOnNotWorkingWithAgency: false,
      },
    ]);
  };

  handlePassthrough = () => {
    if (this.navigator === null) {
      return;
    }

    const navigator = this.navigator;
    const bundleItemIdString = sessionStorage.getItem(TRAINING_CENTER_PASSTHROUGH_BUNDLE_ITEM);
    const bundleDueDateIdString = sessionStorage.getItem(
      TRAINING_CENTER_PASSTHROUGH_BUNDLE_DUE_DATE
    );

    let pages: Page[] = [];

    // no bundleDueDateId in storage
    if (bundleDueDateIdString === null) {
      return;
    }

    const bundleDueDateId = TrainingCenterBundleDueDateId.wrap(
      Number.parseInt(bundleDueDateIdString, 10)
    );

    sessionStorage.removeItem(TRAINING_CENTER_PASSTHROUGH_BUNDLE_DUE_DATE);

    if (bundleItemIdString !== null) {
      sessionStorage.removeItem(TRAINING_CENTER_PASSTHROUGH_BUNDLE_ITEM);

      const bundleItemId = TrainingCenterBundleItemId.wrap(Number.parseInt(bundleItemIdString, 10));

      pages = this.getBundleItemPageStack(bundleDueDateId, bundleItemId);
    } else {
      pages = this.getBundleDueDatePageStack(bundleDueDateId);
    }

    navigator.resetPageStack(pages);
  };

  getBundleDueDatePageStack(bundleDueDateId: TrainingCenterBundleDueDateId): Page[] {
    return [
      {
        type: "Main",
        defaultNavigation: {
          type: "TrainingCenter",
        },
      },
      {
        type: "TrainingCenterBundle",
        bundleDueDateId: bundleDueDateId,
      },
    ];
  }

  getBundleItemPageStack(
    bundleDueDateId: TrainingCenterBundleDueDateId,
    bundleItemId: TrainingCenterBundleItemId
  ): Page[] {
    return [
      {
        type: "Main",
        defaultNavigation: {
          type: "TrainingCenter",
        },
      },
      {
        type: "TrainingCenterBundle",
        bundleDueDateId: bundleDueDateId,
      },
      {
        type: "TrainingCenterBundleItem",
        bundleDueDateId: bundleDueDateId,
        bundleItemId: bundleItemId,
      },
    ];
  }

  componentDidMount() {
    this.handlePromoPopup(this.props.api);
    if (firebase.apps.length === 0) {
      this.initFirebaseAnalytics();
    }
    this.props.api
      .checkEmploymentStatus()
      .then((data) => {
        if (this.navigator !== null) {
          if (data.showApplication) {
            // this.navigator.pushPage({type: "RecruitmentMainPage"}, {animation: "lift"});
            this.navigator.resetPageStack([{ type: "RecruitmentMainPage" }]);
          } else if (this.props.startWithRequestAgencies) {
            this.showRequestAgencyStack();
          }
        }
      })
      .catch(() => {
        if (this.props.startWithRequestAgencies) {
          this.showRequestAgencyStack();
        }
      });
    this.handlePassthrough();
    // Hide the splash screen when the app loads
    SplashScreen.hide();
  }

  async handlePromoPopup(api: API) {
    const result = await api.getPromotionalPopover();
    if (result.popover === null) {
      this.setState({ showPromotionalPopover: null });
    } else {
      const amountShown = await getLocalStorageItem(result.popover.localStorageKey);
      const shouldShowUp = amountShown === null || +amountShown < result.popover.maxDisplayAmount;
      if (shouldShowUp) {
        this.setState({ showPromotionalPopover: result.popover });
      } else {
        this.setState({ showPromotionalPopover: null });
      }
    }
  }

  // TODO This is really ugly. Proper solution is to migrate to Ons.RouterNavigator (instead of Ons.Navigator)
  private navigator: Ons.NavigatorCtrl<Page> | null = null;

  private connectionStatusOverlay!: ConnectionStatusOverlay;
  private appBackgroundManager: AppBackgroundManager;
  private pinUnlockOverlayManager: PinUnlockOverlayManager;

  private authExpiredBinding!: EventSourceBinding;
  private appNeedsUpdateBinding!: EventSourceBinding;
  private userIsBlockedBinding!: EventSourceBinding;

  UNSAFE_componentWillMount() {
    this.connectionStatusOverlay = new ConnectionStatusOverlay(this.props.appState);
    this.connectionStatusOverlay.setConnecting();

    this.authExpiredBinding = this.props.api.authExpired.register(() => {
      ons.notification.alert({
        animation: "none",
        modifier: "material",
        title: "Session Expired",
        message: "You must login again",
        cancelable: true,
      });
      this.logout(false);
    });

    this.appNeedsUpdateBinding = this.props.api.appNeedsUpdate.register(() => {
      this.props.onAppNeedsUpdate();
    });

    this.userIsBlockedBinding = this.props.api.userIsBlocked.register(() => {
      this.props.onUserLocked();
    });

    this.props.api.userIsBlocked.register(() => {
      this.props.onUserLocked();
    });

    this.appBackgroundManager.onResume.register((backgroundMillis) => {
      this.connectionStatusOverlay.setConnecting();

      const BACKGROUND_PIN_LOCKSCREEN_SECONDS = 120;

      if (backgroundMillis > BACKGROUND_PIN_LOCKSCREEN_SECONDS * 1000) {
        this.pinUnlockOverlayManager.handleFirstTime();
        this.pinUnlockOverlayManager.showLockScreen();
      }
    });

    if (!this.props.isAfterRegistration) {
      if ((window as any).disablePinLock === true) {
        localStorage.setItem("medflyt_disable_pin_lock", "true");
      }

      const medflytDisablePinLock = localStorage.getItem("medflyt_disable_pin_lock");
      const lockScreenDeveloperOverride =
        medflytDisablePinLock !== null && medflytDisablePinLock.length > 0;

      if (!lockScreenDeveloperOverride) {
        this.pinUnlockOverlayManager.showLockScreen();
      }
    }
  }

  componentWillUnmount() {
    console.log("componentWillUnmount");

    this.trainingCenterUpdatedBinding.unregister();

    this.pinUnlockOverlayManager.close();
    this.appBackgroundManager.close();
    this.props.appState.close();
    this.connectionStatusOverlay.close();
    this.authExpiredBinding.unregister();
    this.appNeedsUpdateBinding.unregister();
    this.userIsBlockedBinding.unregister();
  }

  async logout(withAPILogout: boolean) {
    if (withAPILogout) {
      const loggedOut = await retryableModalNetworkRequest(async () => {
        await this.props.api.logout();
        removeLocalStorageItem(PIN_CODE_KEY);
        return true;
      }, false);

      if (loggedOut === null) {
        return;
      }
    }

    this.props.onLogout();
  }

  shouldRefreshVisits = (notification: FcmPushNotification) => {
    // If push was sent after any change to any visit, this function should return true
    // (e.g. a new visit was added, a visit was updated, a visit was deleted, etc.)
    // Otherwise, a bug will occur if the user has the app minimized and clicks on the push notification.
    // this bug happens because the app takes the visit/s from the app state, which is not updated
    switch (notification.data.type) {
      case "VISIT_NOTIFICATION":
      case "VISIT_INSTANCE_NOTIFICATION":
      case "VISIT_CANCEL_NOTIFICATION":
      case "VISIT_ASSIGNMENT_NOTIFICATION":
      case "GENERAL_VISIT_ASSIGNMENT_NOTIFICATION":
      case "VISIT_STARTING_SOON_REMINDER":
      case "VISIT_CHAT_MESSAGE":
      case "VISIT_AWAITING_PATIENT_CONTACT":
      case "FLEXIBLE_VISIT_BROADCAST_DETAILS_CHANGED":
      case "COVID_19_VISIT_SCREENING_REMINDER":
      case "CONVERT_IMPORTED_VISIT_REQUEST_RESULT":
      case "FORMS_AWAITING_SUBMISSION_VISIT":
      case "CAREGIVER_VISIT_NOT_SEEN":
      case "NEW_ONBOARDING_VISITS":
      case "VISIT_BROADCAST_STOPPED_BY_AGENCY_MEMBER":
      case "PATIENT_TASK_NOTIFICATION":
        return true;

      case "CAREGIVER_STATUS_CHANGED":
      case "AGENCY_MEMBER_CHAT_MESSAGE":
      case "PATIENT_CHAT_MESSAGE":
      case "NEW_CLUSTER_NOTIFICATION":
      case "AVAILABILITY_ANOMALY_NOTIFICATION":
      case "ORIENTATION_VIDEO_REMINDER_NOTIFICATION":
      case "COMPLIANCE_PROBLEM_NOTIFICATION":
      case "CAREGIVER_GOT_RECRUITED":
      case "RECRUITMENT_APPLICATION_REMINDER_NOTIFICATION":
      case "CAREGIVER_MISSING_DOCUMENTS_REMINDER":
      case "PATIENT_DOCUMENT_UNSUBMITTED_REMINDER_NOTIFICATION":
      case "COVID_19_TRAINING_REMINDER":
      case "COVID_19_SCREENING_REMINDER":
      case "CAREGIVER_PASSPORT_DOCUMENT_VERIFICATION_STATUS_CHANGED":
      case "CAREGIVER_INACTIVE_FOR_3_WEEKS":
      case "FORMS_AWAITING_SUBMISSION_TASK":
      case "TASK_WASNT_SCHEDULED":
      case "MISSED_CLOCK_IN":
      case "MISSED_CLOCK_OUT":
      case "EXPIRED_COMPLIANCE_ITEM":
      case "ABOUT_TO_EXPIRE_COMPLIANCE_ITEM":
      case "PATIENT_ON_VACATION":
      case "DUTY_SHEET_SUBMISSION_REMINDER":
      case "ONBOARDING_REMINDER":
      case "ONBOARDING_DOCUMENT_NOTIFICATION":
      case "COMM_CENTER_TICKET_MESSAGE":
      case "ONBOARDING_CAREGIVER_ENGAGEMENTS":
      case "ONBOARDING_CAREGIVER_ENGAGEMENTS2":
      case "CARE_MOMENTS_CAREGIVER_RANK_ADJUSTED":
      case "INSTA_PAY_FAILURE_NOTIFICATION":
      case "RAFFLE_ENGAGEMENT":
        return false;
    }
  };

  handleFcmNotificationClick = (notification: FcmPushNotification) => {
    if (this.navigator !== null) {
      const navigator = this.navigator;
      const navigateTo = (page: Page) => {
        navigator.pushPage(page, { animation: "lift" });
      };

      if (this.shouldRefreshVisits(notification)) {
        this.props.appState.refreshVisits();
        this.props.appState.refreshSchedule();
        this.props.appState.refreshTaskBroadcasts();
        this.props.appState.refreshPatientTasks();
      }

      switch (notification.data.type) {
        case "PATIENT_CHAT_MESSAGE":
        case "NEW_CLUSTER_NOTIFICATION":
        case "COMPLIANCE_PROBLEM_NOTIFICATION":
        case "VISIT_AWAITING_PATIENT_CONTACT":
        case "CAREGIVER_GOT_RECRUITED":
        case "RECRUITMENT_APPLICATION_REMINDER_NOTIFICATION":
        case "CAREGIVER_MISSING_DOCUMENTS_REMINDER":
        case "PATIENT_DOCUMENT_UNSUBMITTED_REMINDER_NOTIFICATION":
        case "COVID_19_TRAINING_REMINDER":
        case "COVID_19_SCREENING_REMINDER":
        case "COVID_19_VISIT_SCREENING_REMINDER":
        case "CAREGIVER_PASSPORT_DOCUMENT_VERIFICATION_STATUS_CHANGED":
        case "CONVERT_IMPORTED_VISIT_REQUEST_RESULT":
        case "CAREGIVER_INACTIVE_FOR_3_WEEKS":
        case "FORMS_AWAITING_SUBMISSION_TASK":
        case "TASK_WASNT_SCHEDULED":
        case "MISSED_CLOCK_IN":
        case "MISSED_CLOCK_OUT":
        case "FORMS_AWAITING_SUBMISSION_VISIT":
        case "EXPIRED_COMPLIANCE_ITEM":
        case "ABOUT_TO_EXPIRE_COMPLIANCE_ITEM":
        case "CAREGIVER_VISIT_NOT_SEEN":
        case "PATIENT_ON_VACATION":
        case "DUTY_SHEET_SUBMISSION_REMINDER":
        case "ONBOARDING_REMINDER":
        case "NEW_ONBOARDING_VISITS":
        case "ONBOARDING_CAREGIVER_ENGAGEMENTS":
        case "ONBOARDING_DOCUMENT_NOTIFICATION":
        case "VISIT_CANCEL_NOTIFICATION":
        case "VISIT_BROADCAST_STOPPED_BY_AGENCY_MEMBER":
          // Do nothing on click.
          // Don't add the notification type here mindlessly.
          // Only add if it doesn't require action/navigation.
          break;

        case "PATIENT_TASK_NOTIFICATION": {
          const taskInstanceId = notification.data.patientTaskInstanceId;
          // Need to check if the task is broadcast or assigned
          const { appTaskBroadcastsState, appTasksState } = this.props.appState;
          if (isLoading(appTaskBroadcastsState) || isLoading(appTasksState)) {
            return;
          }

          const taskBroadcast = appTaskBroadcastsState.tasks.find(
            (x) => x.taskInstanceId === taskInstanceId
          );
          if (taskBroadcast !== undefined) {
            navigateTo({
              type: "AvailableTaskScreen",
              taskBroadcastId: taskInstanceId,
            });
          }

          const assignedTask = appTasksState.tasks.find((x) => x.id === taskInstanceId);
          if (assignedTask !== undefined) {
            navigateTo({
              type: "PatientTaskInstance",
              initialScheduleDateTimePickerOpen: false,
              patientTaskInstanceId: taskInstanceId,
            });
          }

          break;
        }

        case "FLEXIBLE_VISIT_BROADCAST_DETAILS_CHANGED":
          navigateTo({
            type: "AvailableVisit",
            visitBroadcastId: notification.data.visitId,
          });
          break;

        case "INSTA_PAY_FAILURE_NOTIFICATION":
          navigateTo({ type: "InstaPay" });
          break;

        case "AVAILABILITY_ANOMALY_NOTIFICATION":
          navigateTo({ type: "CaseSettings" });
          break;

        case "CAREGIVER_STATUS_CHANGED":
          navigateTo({
            type: "MyAgencies",
            addAgency: false,
            resetOnFinish: false,
            popOnNotWorkingWithAgency: false,
          });
          break;

        case "VISIT_CHAT_MESSAGE":
          navigateTo({
            type: "AvailableVisit",
            visitBroadcastId: notification.data.visitId,
          });
          break;

        case "AGENCY_MEMBER_CHAT_MESSAGE":
          navigateTo({
            type: "Chat",
            chatRoomId: notification.data.chatRoomId,
            linkToVisit: false,
          });
          break;

        case "VISIT_STARTING_SOON_REMINDER":
        case "VISIT_NOTIFICATION":
          navigateTo({
            type: "AvailableVisit",
            visitBroadcastId: notification.data.visitId,
          });
          break;

        case "VISIT_INSTANCE_NOTIFICATION":
          navigateTo({
            type: "VisitInstance",
            visitInstanceId: notification.data.visitInstanceId,
          });
          break;

        case "VISIT_ASSIGNMENT_NOTIFICATION":
          navigateTo({
            type: "VisitPendingConfirmation",
            visitId: notification.data.visitId,
            isReviewOnly: false,
          });
          break;

        case "ORIENTATION_VIDEO_REMINDER_NOTIFICATION":
          navigateTo({
            type: "Main",
            defaultNavigation: {
              type: "TrainingCenter",
            },
          });
          break;

        case "ONBOARDING_CAREGIVER_ENGAGEMENTS2":
          this.handleOnboardingEngagementNotificationClick(notification.data.payload, navigateTo);
          break;

        case "COMM_CENTER_TICKET_MESSAGE":
          navigateTo({
            type: "ChatRoom",
            ticketId: notification.data.ticketId,
          });
          break;

        case "GENERAL_VISIT_ASSIGNMENT_NOTIFICATION":
          navigateTo({
            type: "Main",
            defaultNavigation: {
              type: "Visits",
              secondaryTab: "Schedule",
            },
          });
          break;

        case "CARE_MOMENTS_CAREGIVER_RANK_ADJUSTED":
          navigateTo({ type: "CareMoments" });
          break;

        case "RAFFLE_ENGAGEMENT":
          navigateTo({ type: "Raffle" });
          break;
      }
    }
  };

  handleOnboardingEngagementNotificationClick(
    payload: OnboardingCaregiversNotificationPayloads[keyof OnboardingCaregiversNotificationPayloads],
    navigateTo: (page: Page) => void
  ) {
    switch (payload.type) {
      case "PROFILE_CREATED_APP_NOT_INSTALLED":
      case "FIRST_REQUESTED_CASE_RECURRING":
      case "FIRST_REQUESTED_CASE_SCHEDULED":
      case "ACTIVE_NOT_REQUESTED_CASES":
        break;
      case "DID_NOT_COMPLETE_I9_SECTION":
        navigateTo({
          type: "OnboardingActivityWithDirection",
          customDirectionParams: {
            type: "Section",
            sectionId: payload.sectionId,
          },
        });
        break;
      case "DOCUMENTS_APPROVED_MISSING_ORIENTATION":
      case "DOCUMENTS_PENDING_MISSING_ORIENTATION":
        navigateTo({
          type: "TrainingCenterBundle",
          bundleDueDateId: payload.bundleDueDateId,
        });
        break;
      case "FILE_APPROVED_MISSING_QUESTIONS":
      case "PROFILE_CREATED_HAS_ANSWERS_NO_DOCS":
      case "PROFILE_CREATED_NO_ANSWERS_AND_DOCS":
      case "PROFILE_CREATED_HAS_ANSWERS_SOME_DOCS":
      case "DID_NOT_COMPLETE_ONBOARDING2_RECURRING":
      case "DID_NOT_COMPLETE_ONBOARDING2_SCHEDULED":
        navigateTo({
          type: "OnboardingActivity",
        });
        break;
      case "HAS_MEDICALS_UPLOADED_NONE":
      case "ITEM_IS_REJECTED":
        navigateTo({
          type: "OnboardingActivityWithDirection",
          customDirectionParams: {
            ...payload,
            type: "Question",
          },
        });
        break;
      case "FIRST_REQUESTED_CASE_ACCEPTED_BY_AGENCY":
        navigateTo({
          type: "VisitInstance",
          visitInstanceId: payload.visitInstanceId,
        });
        break;
      default:
        // We dont want to throw error
        console.warn(`Dont know how to handle notification type ${payload}`);
    }
  }

  handleLanguagePreferenceChange = (languagePreference: TrainingCenterLanguagePreference): void => {
    this.setState({
      languagePreference: languagePreference,
    });
  };

  handleClock = async (
    visitInstanceId: VisitInstanceId,
    clockInOutCallType: ClockInOutCallType,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams
  ) => {
    const success = await this.props.api.setCaregiverVisitInstanceClockInClockOut(
      visitInstanceId,
      clockInOutCallType,
      visitInstanceClockinClockoutRequestParams
    );
    if (success) {
      this.props.appState.setVisitInstanceOrTaskVisitInstanceClockInOutTimeByType(
        visitInstanceId,
        clockInOutCallType,
        LocalDateTime.now()
      );
    }

    return success;
  };

  changeActiveScreenTab = (screenTab: MainScreenTab) => {
    this.setState({ defaultTab: screenTab });
  };

  getAnomalies = () => getAnomaliesArray(this.props.api);

  handleRequestNavigate = (page: Page) => {
    if (this.navigator !== null) {
      this.navigator.pushPage(page, { animation: platformDefaultAnimation() });
    }
  };

  handleRequestNavigateReplace = (page: Page) => {
    if (this.navigator !== null) {
      this.navigator.replacePage(page, { animation: platformDefaultAnimation() });
    }
  };

  handleClickBundle = (bundleDueDateId: TrainingCenterBundleDueDateId) => {
    this.handleRequestNavigate({
      type: "TrainingCenterBundle",
      bundleDueDateId: bundleDueDateId,
    });
  };

  handleNavigateReplaceBundle = (bundleDueDateId: TrainingCenterBundleDueDateId) => {
    this.handleRequestNavigateReplace({
      type: "TrainingCenterBundle",
      bundleDueDateId: bundleDueDateId,
    });
  };

  handleClickAvailableVisit = (visitBroadcastId: VisitBroadcastId) => {
    if (this.navigator !== null) {
      this.props.api.setVisitMoreDetailsViewed(visitBroadcastId);
      this.navigator.pushPage(
        {
          type: "AvailableVisit",
          visitBroadcastId: visitBroadcastId,
        },
        {
          animation: platformDefaultAnimation(),
        }
      );
    }
  };

  handleClickOnboardingVisit = (onboardingVisitId: OnboardingVisitId) => {
    if (this.navigator !== null) {
      this.navigator.pushPage({
        type: "OnboardingVisit",
        onboardingVisitId,
      });
    }
  };

  handleClickAvailableTask = (taskBroadcastId: PatientTaskInstanceId) => {
    if (this.navigator !== null) {
      this.props.api.setTaskBroadcastMoreDetailsViewed(taskBroadcastId);
      this.navigator.pushPage(
        {
          type: "AvailableTaskScreen",
          taskBroadcastId: taskBroadcastId,
        },
        {
          animation: platformDefaultAnimation(),
        }
      );
    }
  };

  handleClickAssignedTask = (taskInstanceId: PatientTaskInstanceId) => {
    if (this.navigator !== null) {
      this.navigator.pushPage(
        {
          type: "PatientTaskInstance",
          patientTaskInstanceId: taskInstanceId,
          initialScheduleDateTimePickerOpen: false,
        },
        {
          animation: platformDefaultAnimation(),
        }
      );
    }
  };

  handleClickConfirmAssignment = (visitId: VisitBroadcastId, isReviewOnly: boolean) => {
    this.handleRequestNavigate({
      type: "VisitPendingConfirmation",
      visitId,
      isReviewOnly,
    });
  };

  renderMainActivity(params: {
    defaultNavigation?: MainScreenTabNavigation;
    scrollToScheduleDate?: Instant;
  }) {
    return (
      <MainActivityController
        v1Props={{
          api: this.props.api,
          appState: this.props.appState,
          defaultNavigation: params.defaultNavigation,
          activeScreenTab: this.state.defaultTab,
          onChangeActiveScreenTab: this.changeActiveScreenTab,
          languagePreference: this.state.languagePreference,
          scrollToScheduleDate: params.scrollToScheduleDate,
          chatSystemStrategies: this.props.chatSystemStrategies,
          getAnomalies: this.getAnomalies,
          hideFinishOnboardingModal: this.state.showPromotionalPopover !== null,
          onRequestNavigate: this.handleRequestNavigate,
          onClickNewChatWithMedflyt: this.handleClickNewChat,
          onClickAvailableVisit: this.handleClickAvailableVisit,
          onClickAvailableTask: this.handleClickAvailableTask,
          onClickOnboardingVisit: this.handleClickOnboardingVisit,
          onClickConfirmAssignment: this.handleClickConfirmAssignment,
          onClock: this.handleClock,
        }}
        v2Props={{
          api: this.props.api,
          appState: this.props.appState,
          hideFinishOnboardingModal: this.state.showPromotionalPopover !== null,
          isRouteStackEmpty: this.navigator?.routes.length === 1,
          isPromoOpen:
            isLoading(this.state.showPromotionalPopover) ||
            this.state.showPromotionalPopover !== null,
          onRequestNavigate: this.handleRequestNavigate,
          chatSystemStrategies: this.props.chatSystemStrategies,
          onClickAvailableVisit: this.handleClickAvailableVisit,
          onClickAvailableTask: this.handleClickAvailableTask,
          onClickAssignedTask: this.handleClickAssignedTask,
          onClickOnboardingVisit: this.handleClickOnboardingVisit,
          onClickNewChatWithMedflyt: this.handleClickNewChat,
          onClickConfirmAssignment: this.handleClickConfirmAssignment,
          onClock: this.handleClock,
        }}
      />
    );
  }

  renderProfileActivity(navigator: Ons.NavigatorCtrl<Page>) {
    return (
      <ProfileActivity
        api={this.props.api}
        appState={this.props.appState}
        onBackClick={() => navigator.popPage()}
      />
    );
  }

  renderSettingsActivity(navigator: Ons.NavigatorCtrl<Page>) {
    return (
      <SettingsActivity
        appState={this.props.appState}
        onClickGoBack={() => navigator.popPage()}
        onClickNotifications={() =>
          navigator.pushPage(
            {
              type: "SettingsNotifications",
            },
            { animation: "slide" }
          )
        }
        onClickProfile={() =>
          navigator.pushPage(
            {
              type: "Profile",
            },
            { animation: "slide" }
          )
        }
        onClickSocial={() =>
          navigator.pushPage(
            {
              type: "SettingsSocial",
            },
            { animation: "slide" }
          )
        }
        onClickReferral={() =>
          navigator.pushPage(
            {
              type: "ReferralCode",
            },
            { animation: "slide" }
          )
        }
        onClickGeneral={() =>
          navigator.pushPage(
            {
              type: "SettingsGeneral",
            },
            { animation: "slide" }
          )
        }
        onClickAgencies={() =>
          navigator.pushPage(
            {
              type: "MyAgencies",
              addAgency: false,
              resetOnFinish: false,
              popOnNotWorkingWithAgency: false,
            },
            { animation: "slide" }
          )
        }
        onClickBankInformation={() =>
          navigator.pushPage({ type: "EditBankInformation" }, { animation: "slide" })
        }
        onClickSignOut={() => this.logout(true)}
        onClickInformationCenter={(caregiver) =>
          navigator.pushPage(
            {
              type: "InformationCenter",
              caregiver: caregiver,
            },
            { animation: "slide" }
          )
        }
      />
    );
  }

  renderSettingsNotificationsActivity(navigator: Ons.NavigatorCtrl<Page>) {
    return (
      <SettingsNotificationActivity onBackClick={() => navigator.popPage()} api={this.props.api} />
    );
  }

  renderTrainingCenterBundleActivity(
    navigator: Ons.NavigatorCtrl<Page>,
    bundleDueDateId: TrainingCenterBundleDueDateId
  ) {
    return (
      <TrainingCenterBundleActivity
        languagePreference={this.state.languagePreference}
        appState={this.props.appState}
        onClickGoBack={() => navigator.popPage()}
        api={this.props.api}
        bundleDueDateId={bundleDueDateId}
        onLanguagePreferenceChange={this.handleLanguagePreferenceChange}
        allLanguages={this.state.allLanguages}
        onRequestNavigateToBundleItem={(bundleItem, bundle) => {
          navigator.pushPage({
            type: "TrainingCenterBundleItem",
            bundleDueDateId: bundle.bundleDueDateId,
            bundleItemId: bundleItem.bundleItemId,
          });
        }}
        onBundleNotFound={() => {
          this.setState({ defaultTab: "TrainingCenter" }, () => {
            navigator.popPage({ animation: "fade" });
          });
        }}
        onDragDown={() => navigator.popPage({ animation: "fade" })}
      />
    );
  }

  renderTrainingCenterBundleItemActivity(
    navigator: Ons.NavigatorCtrl<Page>,
    bundleDueDateId: TrainingCenterBundleDueDateId,
    bundleItemId: TrainingCenterBundleItemId
  ) {
    return (
      <TrainingCenterBundleItemActivity
        languagePreference={this.state.languagePreference}
        appState={this.props.appState}
        onBackClick={(animation) => navigator.popPage({ animation: animation })}
        api={this.props.api}
        bundleDueDateId={bundleDueDateId}
        bundleItemId={bundleItemId}
        onLanguagePreferenceChange={this.handleLanguagePreferenceChange}
        allLanguages={this.state.allLanguages}
        onAgencyMemberChatClick={async (agencyMemberId, initialMessage: string) => {
          const chatRoom = await getAgencyMemberChatRoomId(
            this.props.api,
            this.props.appState.chatState,
            agencyMemberId
          );
          if (chatRoom !== null) {
            navigator.pushPage(
              {
                type: "Chat",
                chatRoomId: chatRoom,
                linkToVisit: false,
                initialMessage: initialMessage,
              },
              { animation: "slide" }
            );
          }
        }}
        onBundleItemNotFound={() => navigator.popPage({ animation: "fade" })}
        onSubmitTestSuccessConfirm={() => {
          if (isLoading(this.props.appState.appTrainingCenterState)) {
            return;
          }

          const bundle = this.props.appState.appTrainingCenterState.bundles.find(
            (b) => b.bundleDueDateId === bundleDueDateId
          );

          if (bundle === undefined) {
            return;
          }

          const nextBundleItem = bundle.bundleItems.find(
            (i) => i.bundleItemId !== bundleItemId && !i.itemCompleted && !i.itemExempt
          );

          if (nextBundleItem === undefined) {
            navigator.popPage();
          } else {
            navigator.popPage({
              callback: () => {
                navigator.pushPage(
                  {
                    type: "TrainingCenterBundleItem",
                    bundleItemId: nextBundleItem.bundleItemId,
                    bundleDueDateId: bundleDueDateId,
                  },
                  { animation: "slide" }
                );
              },
              animation: "none",
            });
          }
        }}
        onRequestNavigateToNewChat={() =>
          this.handleClickNewChat({
            type: "General",
          })
        }
      />
    );
  }

  renderMedflytPassportScreen(navigator: Ons.NavigatorCtrl<Page>) {
    return (
      <MedflytPassportActivity
        getMedflytPassportCategories={() => this.props.api.getMedflytPassportCategories()}
        onCategoriesUpdated={() => this.props.api.categoriesUpdated}
        onDocumentTypeClick={(agencyId, documentType, language) =>
          navigator.pushPage(
            {
              type: "MedflytPassportDocuments",
              agencyId: agencyId,
              documentType: documentType,
              language: language,
            },
            { animation: "slide" }
          )
        }
        onBackClick={() => navigator.popPage()}
      />
    );
  }

  renderMedflytPassportCategoryScreen(
    navigator: Ons.NavigatorCtrl<Page>,
    category: PassportDocumentCategory,
    language: OrientationLanguages
  ) {
    return (
      <MedflytPassportCategoryScreen
        category={category}
        api={this.props.api}
        language={language}
        onBackClick={() => navigator.popPage()}
        onDocumentTypeClick={(agencyId, documentType) =>
          navigator.pushPage(
            {
              type: "MedflytPassportDocuments",
              agencyId: agencyId,
              documentType: documentType,
              language: language,
            },
            { animation: "slide" }
          )
        }
      />
    );
  }

  renderMedflytPassportDocumentsScreen(
    navigator: Ons.NavigatorCtrl<Page>,
    agencyId: AgencyId | null,
    documentType: PassportDocumentType | AgencyDocumentType,
    language: OrientationLanguages
  ) {
    return (
      <MedflytPassportDocumentsScreen
        language={language}
        documentType={documentType}
        agencyId={agencyId}
        api={this.props.api}
        onBackClick={() => navigator.popPage()}
        onDocumentViewClick={(url: string | null) =>
          navigator.pushPage(
            { type: "UploadedFileViewer", title: "Main", url: url },
            { animation: "slide" }
          )
        }
      />
    );
  }

  renderUploadedFileActivity(navigator: Ons.NavigatorCtrl<Page>, url: string | null) {
    return <UploadedFileViewerScreen onBackClick={() => navigator.popPage()} url={url} />;
  }

  renderComplianceScreen(navigator: Ons.NavigatorCtrl<Page>) {
    const data = [
      { name: "Flu vaccine", expires: LocalDate.now().plusDays(55), done: true },
      { name: "Rubeola Immune", expires: LocalDate.now().plusDays(254), done: false },
      { name: "Flu Vaccination", expires: LocalDate.now().plusDays(525), done: false },
      { name: "Flu vaccine", expires: LocalDate.now().plusDays(55), done: true },
      { name: "Rubeola Immune", expires: LocalDate.now().plusDays(254), done: false },
      { name: "Flu Vaccination", expires: LocalDate.now().plusDays(525), done: false },
    ];
    return <ComplianceActivity data={data} onBackClick={() => navigator.popPage()} />;
  }

  renderSettingsSocialActivity(navigator: Ons.NavigatorCtrl<Page>) {
    return <SettingsSocialScreen onClickGoBack={() => navigator.popPage()} />;
  }

  renderSettingsHelpActivity(navigator: Ons.NavigatorCtrl<Page>) {
    let agencies: string[];
    if (isLoading(this.props.appState.appAgenciesState)) {
      agencies = [];
    } else {
      agencies = [...this.props.appState.appAgenciesState.agencies.values()].map(
        (x) => x.agency.name
      );
      agencies.sort();
    }

    const handleClickChatSupport = () => {
      const caregiver = this.props.appState.appCaregiverState;
      if (isLoading(caregiver)) {
        return;
      }
      if (caregiver.caregiver.shouldShowNewChat) {
        return this.handleClickNewChat({
          type: "General",
        });
      }
      return this.handleClickChatWithAgencyMember(
        AgencyMemberId.wrap(1726) /* default support guy */
      );
    };

    return (
      <SettingsHelpActivity
        appState={this.props.appState}
        getAnomalies={(noEncode: boolean) => getAnomalies(this.props.api, noEncode)}
        onClickChatSupport={handleClickChatSupport}
        onClickGoBack={() => navigator.popPage()}
      />
    );
  }

  renderSettingsGeneralActivity(navigator: Ons.NavigatorCtrl<Page>) {
    return <SettingsGeneralScreen onClickGoBack={() => navigator.popPage()} />;
  }

  renderMyAgenciesActivity(
    navigator: Ons.NavigatorCtrl<Page>,
    addAgency: boolean,
    resetOnFinish: boolean
  ) {
    if (!addAgency) {
      return (
        <MyAgenciesActivity
          api={this.props.api}
          appState={this.props.appState}
          onClickGoBack={() => navigator.popPage()}
          onClickAddAgency={() =>
            this.props.api.checkEmploymentStatus().then((data) => {
              if (data.showApplication) {
                navigator.resetPageStack([{ type: "RecruitmentMainPage" }]);
              } else {
                navigator.pushPage(
                  {
                    type: "MyAgencies",
                    addAgency: true,
                    resetOnFinish: false,
                    popOnNotWorkingWithAgency: false,
                  },
                  { animation: "slide" }
                );
              }
            })
          }
          onClickChatWithMedflytAgency={() =>
            this.handleClickNewChat({
              type: "General",
            })
          }
          onClickChatWithAgencyMember={this.handleClickChatWithAgencyMember}
        />
      );
    } else {
      let finishRequest;
      if (resetOnFinish) {
        finishRequest = () => {
          navigator.resetPageStack(
            [
              {
                type: "Main",
                defaultNavigation: {
                  type: "Visits",
                },
              },
            ],
            { animation: "lift" }
          );
          setTimeout(() => {
            const cancelApplicationEvent = new CustomEvent<{}>("cancelApplicationFill");
            document.dispatchEvent(cancelApplicationEvent);
          }, 300);
        };
      } else {
        finishRequest = () => {
          navigator.popPage();
        };
      }
      return (
        <AgencyRequestActivity
          api={this.props.api}
          appState={this.props.appState}
          onBackClick={() => navigator.popPage()}
          caregiver={
            isLoading(this.props.appState.appCaregiverState)
              ? loading()
              : this.props.appState.appCaregiverState.caregiver
          }
          onFinishRequest={finishRequest}
        />
      );
    }
  }

  renderMedFlytChallengeActivity(navigator: Ons.NavigatorCtrl<Page>) {
    return (
      <MedFlytChallengeActivity
        api={this.props.api}
        appState={this.props.appState}
        onBackClick={() => navigator.popPage()}
      />
    );
  }

  handleClickChatWithAgencyMember = async (agencyMemberId: AgencyMemberId) => {
    const agencies = this.props.appState.appAgenciesState;
    if (isLoading(agencies)) {
      return;
    }
    const agency = fmap(agencies.allAgencyMembers.get(agencyMemberId)?.agencyId, (agencyId) =>
      agencies.agencies.get(agencyId)
    );
    if (agency?.agency.isMedflytAgency === true) {
      this.handleClickNewChat({ type: "General" });
      return;
    }

    const chatRoomId = await getAgencyMemberChatRoomId(
      this.props.api,
      this.props.appState.chatState,
      agencyMemberId
    );

    if (chatRoomId !== null && isDefined(this.navigator)) {
      this.navigator.pushPage(
        {
          type: "Chat",
          chatRoomId: chatRoomId,
          linkToVisit: false,
        },
        { animation: "slide" }
      );
    }
  };

  handleClickChatWithAgencyAvailableVisit = async (
    patientId: PatientId,
    visitBroadcastId: VisitBroadcastId
  ) => {
    const caregiverState = this.props.appState.appCaregiverState;

    if (isLoading(caregiverState)) {
      return;
    }

    if (caregiverState.caregiver.shouldShowNewChat) {
      this.handleClickNewChat({
        type: "VisitBroadcast",
        patientId,
        visitBroadcastId,
      });
      return;
    }

    const chatRoomId = await getPatientChatRoomId(
      this.props.api,
      this.props.appState.chatState,
      patientId
    );

    if (chatRoomId !== null && isDefined(this.navigator)) {
      this.navigator.pushPage(
        {
          type: "Chat",
          chatRoomId: chatRoomId,
          linkToVisit: true,
        },
        { animation: "slide" }
      );
    }
  };

  handleClickChatWithAgencyAssingedVisit = async (
    patientId: PatientId,
    visitInstanceId: VisitInstanceId
  ) => {
    const caregiverState = this.props.appState.appCaregiverState;

    if (isLoading(caregiverState)) {
      return;
    }

    if (caregiverState.caregiver.shouldShowNewChat) {
      this.handleClickNewChat({
        type: "AssigedVisit",
        patientId,
        visitInstanceId,
      });
      return;
    }

    const chatRoomId = await getPatientChatRoomId(
      this.props.api,
      this.props.appState.chatState,
      patientId
    );

    if (chatRoomId !== null && isDefined(this.navigator)) {
      this.navigator.pushPage(
        {
          type: "Chat",
          chatRoomId: chatRoomId,
          linkToVisit: true,
        },
        { animation: "slide" }
      );
    }
  };

  handleClickChatWithAgency = async (patientId: PatientId) => {
    const caregiverState = this.props.appState.appCaregiverState;

    if (isLoading(caregiverState)) {
      return;
    }

    if (caregiverState.caregiver.shouldShowNewChat) {
      this.handleClickNewChat({ type: "General" });
      return;
    }

    const chatRoomId = await getPatientChatRoomId(
      this.props.api,
      this.props.appState.chatState,
      patientId
    );

    if (chatRoomId !== null && isDefined(this.navigator)) {
      this.navigator.pushPage(
        {
          type: "Chat",
          chatRoomId: chatRoomId,
          linkToVisit: true,
        },
        { animation: "slide" }
      );
    }
  };

  handleClickNewChat = async (params: CommCenterTicketCreateParams) => {
    const agenciesState = this.props.appState.appAgenciesState;

    if (isLoading(agenciesState)) {
      return;
    }

    const agency = [...agenciesState.agencies.values()].find((x) => x.agency.isMedflytAgency);

    if (agency === undefined) {
      return;
    }

    const ticketId = await (async () => {
      switch (params.type) {
        case "AssigedVisit": {
          const { ticket } = await this.props.api.createCommCenterTicketForAssignedVisit(params);
          return ticket.id;
        }
        case "VisitBroadcast": {
          const { ticket } = await this.props.api.createCommCenterTicketForAvailabelVisit(params);
          return ticket.id;
        }
        case "General":
        default: {
          const { ticket } = await this.props.api.createCommCenterTicket({
            agencyId: agency.id,
            source: "MOBILE_CHAT",
          });
          return ticket.id;
        }
      }
    })();

    this.navigator?.pushPage({ type: "ChatRoom", ticketId });
  };

  handleClickGoBack = () => {
    if (isDefined(this.navigator)) {
      this.navigator.popPage({ animation: "slide" });
    }
  };

  renderAvailableVisitActivity(visitBroadcastId: VisitBroadcastId) {
    return (
      <AvailableVisitActivity
        appState={this.props.appState}
        api={this.props.api}
        visitBroadcastId={visitBroadcastId}
        onClickChatWithAgency={this.handleClickChatWithAgencyAvailableVisit}
        onRequestNavigate={this.handleRequestNavigate}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderOnboardingVisitActivity(onboardingVisitId: OnboardingVisitId) {
    return (
      <OnboardingVisitActivity
        appState={this.props.appState}
        api={this.props.api}
        onboardingVisitId={onboardingVisitId}
        onClickChatWithAgency={this.handleClickChatWithAgency}
        onRequestNavigate={this.handleRequestNavigate}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderAvailableTaskActivity(taskBroadcastId: PatientTaskInstanceId) {
    return (
      <AvailableTaskActivity
        appState={this.props.appState}
        api={this.props.api}
        patientTaskBroadcastId={taskBroadcastId}
        onClickChatWithAgency={this.handleClickChatWithAgency}
        onRequestNavigate={this.handleRequestNavigate}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderVisitPendingConfirmation(visitId: VisitBroadcastId, isReviewOnly: boolean) {
    return (
      <VisitPendingConfirmationActivity
        appState={this.props.appState}
        api={this.props.api}
        isReviewOnly={isReviewOnly}
        visitId={visitId}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderTimesheetActivity(visitInstanceId: VisitInstanceId) {
    return (
      <TimesheetActivity
        appState={this.props.appState}
        api={this.props.api}
        visitInstanceId={visitInstanceId}
        onRequestNavigate={this.handleRequestNavigate}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderOnboardingActivity(
    navigator: Ons.NavigatorCtrl<Page>,
    customDirectionParams: CustomOnboardingRedirectionParams | null
  ) {
    return (
      <WithCaregiver appState={this.props.appState}>
        {(caregiver) => (
          <OnboardingActivity
            api={this.props.api}
            caregiver={caregiver}
            appState={this.props.appState}
            customDirectionParams={customDirectionParams}
            handleNavigateReplaceBundle={this.handleNavigateReplaceBundle}
            languagePreference={this.state.languagePreference}
            getApplicationBanner={() => this.props.api.getApplicationBanner()}
            getCountNewMessages={caregiverChatStrategies(caregiver.id).numNewMessagesStrategy}
            onRequestNavigate={this.handleRequestNavigate}
            onClickChat={() => {
              this.handleClickNewChat({
                type: "General",
              });
            }}
            onClickBundle={this.handleClickBundle}
            onClickGoBack={() => navigator.popPage()}
            onRequestNavigateToBundle={(bundle) => {
              navigator.pushPage({
                type: "TrainingCenterBundle",
                bundleDueDateId: bundle.bundleDueDateId,
              });
            }}
          />
        )}
      </WithCaregiver>
    );
  }

  renderOnboardingSectionActivity(
    sections: HRSection[],
    section: HRSection,
    caregiverApplicationQuery: UseQueryResult<HRApplicationForm, unknown>,
    language: string,
    customDirectionParams: CustomOnboardingRedirectionParams | null
  ) {
    return (
      <OnboardingSectionActivity
        appState={this.props.appState}
        api={this.props.api}
        sections={sections}
        section={section}
        language={language}
        caregiverApplicationQuery={caregiverApplicationQuery}
        customDirectionParams={customDirectionParams}
        onClickGoBack={this.handleClickGoBack}
        onRequestNavigate={this.handleRequestNavigate}
        onRequestNavigateReplace={this.handleRequestNavigateReplace}
      />
    );
  }

  renderMyMedflytComplianceActivity() {
    return (
      <MyMedflytComplianceActivity
        api={this.props.api}
        appState={this.props.appState}
        languagePreference={this.state.languagePreference}
        onRequestNavigate={this.handleRequestNavigate}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderComplianceItemActivity(documentTypeId: CaregiverDocumentTypeId) {
    return (
      <ComplianceItemActivity
        api={this.props.api}
        documentTypeId={documentTypeId}
        onClickGoBack={this.handleClickGoBack}
        onRequestNavigate={this.handleRequestNavigate}
      />
    );
  }

  renderCompliantItemsActivity(params: {
    onClickComplianceItem: (item: CaregiverComplianceItemMobile) => void;
    onClickReferenceItem: (
      documentTypeId: CaregiverDocumentTypeId,
      referenceData: CaregiverComplianceReferenceDocumentData | null,
      complianceStatus: ComplianceStatus
    ) => void;
  }) {
    return (
      <CompliantItemsActivity
        api={this.props.api}
        onClickGoBack={this.handleClickGoBack}
        {...params}
      />
    );
  }

  renderComplianceDocumentActivity(document: CaregiverUploadDocumentMobile) {
    return (
      <ComplianceDocumentActivity
        api={this.props.api}
        document={document}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderChatRoomActivity(id: CommCenterTicketId) {
    return (
      <ChatRoomActivity
        api={this.props.api}
        appState={this.props.appState}
        ticketId={id}
        onRequestNavigate={this.handleRequestNavigate}
        onRequestNavigateReplace={this.handleRequestNavigateReplace}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderComplianceDocumentForm(documentTypeId: CaregiverDocumentTypeId) {
    return (
      <ComplianceFormActivity
        api={this.props.api}
        documentTypeId={documentTypeId}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderComplianceI9() {
    return <ComplianceI9Activity api={this.props.api} onRequestGoBack={this.handleClickGoBack} />;
  }

  renderInstaPayActivity() {
    return (
      <InstaPayActivity
        api={this.props.api}
        appState={this.props.appState}
        onClickGoBack={this.handleClickGoBack}
        onRequestGoToMain={this.resetToMainStack}
        onRequestNavigate={this.handleRequestNavigate}
      />
    );
  }

  renderInstaPayHistoryActivity() {
    return (
      <InstaPayHistoryActivity
        api={this.props.api}
        appState={this.props.appState}
        onClickGoBack={this.handleClickGoBack}
        onRequestNavigate={this.handleRequestNavigate}
      />
    );
  }

  renderSelectInstaPayVisitsActivity(weekNum: number) {
    return (
      <SelectInstaPayVisitsActivity
        api={this.props.api}
        weekNum={weekNum}
        onClickGoBack={this.handleClickGoBack}
        onRequestGoToMain={this.resetToMainStack}
        onRequestNavigate={this.handleRequestNavigate}
      />
    );
  }

  renderFillBankInformationActivity(params: { visitInstanceIds: VisitInstanceId[] }) {
    return (
      <FillBankInformationActivity
        api={this.props.api}
        visitInstanceIds={params.visitInstanceIds}
        onRequestGoToMain={this.resetToMainStack}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderEditBankInformationActivity() {
    return (
      <EditBankInformationActivity
        api={this.props.api}
        onRequestGoToMain={this.resetToMainStack}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderInformationCenterActivity(caregiver: AppCaregiver | Loading) {
    return (
      <InformationCenterActivity
        caregiver={caregiver}
        onClickGoBack={this.handleClickGoBack}
        onRequestNavigate={this.handleRequestNavigate}
      />
    );
  }

  renderCarebackPointsActivity(caregiver: AppCaregiver | Loading) {
    return (
      <CarebackPointsActivity
        caregiver={caregiver}
        api={this.props.api}
        onClickGoBack={this.handleClickGoBack}
        onRequestNavigate={this.handleRequestNavigate}
      />
    );
  }

  renderImportantInformationActivity() {
    return (
      <ImportantInformationActivity
        onClickGoBack={this.handleClickGoBack}
        onRequestNavigate={this.handleRequestNavigate}
      />
    );
  }

  renderVisitInstanceActivity(visitInstanceId: VisitInstanceId) {
    return (
      <VisitInstanceActivity
        api={this.props.api}
        appState={this.props.appState}
        visitInstanceId={visitInstanceId}
        onClickGoBack={this.handleClickGoBack}
        onRequestNavigate={this.handleRequestNavigate}
        onClickChatWithAgency={this.handleClickChatWithAgencyAssingedVisit}
      />
    );
  }

  renderDocumentDownloadActivity(documentType: string) {
    return (
      <DocumentDownloadActivity
        documentType={documentType}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderNotificationActivity(navigator: Ons.NavigatorCtrl<Page>) {
    const navigateTo = (page: Page) => {
      navigator.pushPage(page, { animation: "lift" });
    };
    return (
      <NotificationActivity
        api={this.props.api}
        appState={this.props.appState}
        onBackClick={() => navigator.popPage()}
        onOnboardingEngagementNotificationClick={(payload) =>
          this.handleOnboardingEngagementNotificationClick(payload, navigateTo)
        }
        onVisitNotificationClick={(visitId) => {
          this.handleClickAvailableVisit(visitId);
        }}
        onGeneralVisitAssignmentNotificationClick={() =>
          navigateTo({
            type: "Main",
            defaultNavigation: {
              type: "Visits",
              secondaryTab: "Schedule",
            },
          })
        }
      />
    );
  }

  renderChatActivity(
    navigator: Ons.NavigatorCtrl<Page>,
    chatRoomId: ChatRoomId,
    linkToVisit: boolean,
    initialMessage?: string
  ) {
    return (
      <ChatActivity
        api={this.props.api}
        appState={this.props.appState}
        chatRoomId={chatRoomId}
        onBackClick={() => navigator.popPage()}
        initialMessage={initialMessage}
        goToVisit={
          linkToVisit
            ? (params) => {
                switch (params.type) {
                  case "Available":
                    navigator.pushPage(
                      {
                        type: "AvailableVisit",
                        visitBroadcastId: params.visitId,
                      },
                      { animation: "slide" }
                    );
                    break;
                  case "Onboarding":
                    navigator.pushPage(
                      {
                        type: "OnboardingVisit",
                        onboardingVisitId: params.visitId,
                      },
                      { animation: "slide" }
                    );
                    break;
                  case "VisitInstance":
                    navigator.pushPage(
                      {
                        type: "VisitInstance",
                        visitInstanceId: params.visitInstanceId,
                      },
                      { animation: "slide" }
                    );
                    break;
                  default:
                    assertNever(params);
                }
              }
            : null
        }
      />
    );
  }

  renderReferralCodeActivity(navigator: Ons.NavigatorCtrl<Page>) {
    return (
      <ReferralCodeActivity
        api={this.props.api}
        appState={this.props.appState}
        onBackClick={() => navigator.popPage()}
      />
    );
  }

  renderCaseSettings(navigator: Ons.NavigatorCtrl<Page>) {
    return (
      <CaseSettingsActivity
        onClickGoBack={() => navigator.popPage()}
        api={this.props.api}
        appState={this.props.appState}
      />
    );
  }

  renderCaseSettingsV2(navigator: Ons.NavigatorCtrl<Page>, showMyStaffingPreferences: boolean) {
    return (
      <CaseSettingsV2Activity
        showMyStaffingPreferencesOption={showMyStaffingPreferences}
        onClickMyAvailability={() =>
          navigator.pushPage(
            {
              type: "CaseSettings",
            },
            { animation: "slide" }
          )
        }
        onClickMyStaffingPreferences={() =>
          navigator.pushPage(
            {
              type: "MyStaffingPreferences",
            },
            { animation: "slide" }
          )
        }
        onClickGoBack={() => navigator.popPage()}
      />
    );
  }

  renderMyStaffingPreferences(navigator: Ons.NavigatorCtrl<Page>) {
    if (isLoading(this.props.appState.appCaregiverState)) {
      return <></>;
    }

    return (
      <MyStaffingPreferencesActivity
        api={this.props.api}
        caregiver={this.props.appState.appCaregiverState}
        onClickGoBack={() => navigator.popPage()}
      />
    );
  }

  renderAddChat(navigator: Ons.NavigatorCtrl<Page>) {
    return (
      <AddChatActivity
        appState={this.props.appState}
        api={this.props.api}
        onBackClick={() => navigator.popPage()}
        onAddChat={this.handleClickChatWithAgencyMember}
        onAddChatMedflytAgency={() =>
          this.handleClickNewChat({
            type: "General",
          })
        }
      />
    );
  }

  renderRescheduleRequest(
    navigator: Ons.NavigatorCtrl<Page>,
    scheduledVisitDetails: VisitInstanceDetails,
    isCancel: boolean
  ) {
    return (
      <RescheduleRequestActivity
        api={this.props.api}
        onBackClick={() => {
          navigator.popPage();
        }}
        isCancel={isCancel}
        scheduledVisit={scheduledVisitDetails}
      />
    );
  }

  renderEditRequests(navigator: Ons.NavigatorCtrl<Page>) {
    return (
      <EditRequestsActivity
        appState={this.props.appState}
        onBackClick={() => {
          navigator.popPage();
        }}
      />
    );
  }

  renderScheduledVisitDocScreen(
    navigator: Ons.NavigatorCtrl<Page>,
    requiredDocument: CaregiverPatientDocument,
    patientId: PatientId,
    updateDocuments?: () => void
  ) {
    return (
      <ScheduledVisitDocScreenActivity
        api={this.props.api}
        appState={this.props.appState}
        onBackClick={() => {
          if (updateDocuments !== undefined) {
            updateDocuments();
          }
          navigator.popPage();
        }}
        requiredDocument={requiredDocument}
        patientId={patientId}
        onClickAddPhysician={() => navigator.pushPage({ type: "AddPatientPhysician", patientId })}
      />
    );
  }

  renderVisitPlanOfCareScreen(
    navigator: Ons.NavigatorCtrl<Page>,
    visitInstanceId: VisitInstanceId,
    init: InitPlanOfCare,
    updatePlanOfCare: () => void
  ) {
    return (
      <VisitPlanOfCareActivity
        api={this.props.api}
        scheduledVisitId={visitInstanceId}
        init={init}
        onPlanOfCareSubmitted={updatePlanOfCare}
        onBackClick={() => navigator.popPage()}
      />
    );
  }

  renderTaskPlanOfCareScreen(
    navigator: Ons.NavigatorCtrl<Page>,
    taskInstanceId: PatientTaskInstanceId,
    init: InitPlanOfCare,
    updatePlanOfCare: () => void
  ) {
    return (
      <TaskPlanOfCareActivity
        api={this.props.api}
        taskInstanceId={taskInstanceId}
        onPlanOfCareSubmitted={updatePlanOfCare}
        onBackClick={() => navigator.popPage()}
        init={init}
      />
    );
  }

  renderRecruitementMainScreen(navigator: Ons.NavigatorCtrl<Page>) {
    return (
      <RecruitmentMainScreen
        onWorkingWithAnAgencyClick={() => {
          navigator.pushPage({
            type: "MyAgencies",
            addAgency: true,
            resetOnFinish: true,
            popOnNotWorkingWithAgency: true,
          });
        }}
        caregiver={this.props.appState.appCaregiverState}
        onApplicationClick={() =>
          navigator.pushPage(
            {
              type: "RecruitmentFormPage",
            },
            { animation: "slide" }
          )
        }
        onLaterClick={() => {
          navigator.resetPageStack([{ type: "Main" }], { animation: "lift" });
          setTimeout(() => {
            const cancelApplicationEvent = new CustomEvent<{}>("cancelApplicationFill");
            document.dispatchEvent(cancelApplicationEvent);
          }, 300);
        }}
      />
    );
  }

  renderRecruitementFormScreen(navigator: Ons.NavigatorCtrl<Page>) {
    return (
      <RecruitmentFormActivity
        api={this.props.api}
        onBackClick={() => navigator.popPage()}
        onSubmitted={() => {
          navigator.resetPageStack([{ type: "Main" }], { animation: "lift" });
          setTimeout(() => {
            const cancelApplicationEvent = new CustomEvent<{}>("cancelApplicationFill");
            document.dispatchEvent(cancelApplicationEvent);
          }, 300);
        }}
        caregiver={this.props.appState.appCaregiverState}
      />
    );
  }

  renderRescheduleScreen(
    navigator: Ons.NavigatorCtrl<Page>,
    params: ResceduleVisitInstanceDetails,
    patientPhoneNumber: string | null,
    onChosenDate: (startTime: LocalDateTime, endTime: LocalDateTime) => Promise<string | null>
  ) {
    return (
      <RescheduleScreen
        params={params}
        patientPhoneNumber={patientPhoneNumber}
        onChosenDate={onChosenDate}
        onBackClick={() => navigator.popPage()}
      />
    );
  }

  renderCovidPage(
    navigator: Ons.NavigatorCtrl<Page>,
    coronaReports: CoronaReportAllQuestionsResponse
  ) {
    return (
      <MedflytCovidActivity
        onBackClick={() => navigator.popPage()}
        agencies={this.props.appState.appAgenciesState}
        onSubmit={(answers: CoronaReportSubmitParams, visitInstanceId: VisitInstanceId | null) =>
          this.props.api.submitCoronaReport(visitInstanceId, answers)
        }
        appState={this.props.appState}
        coronaReports={coronaReports}
        api={this.props.api}
        onAgencyMemberChatClick={this.handleClickChatWithAgencyMember}
      />
    );
  }

  renderConvertImportedVisitScreen(
    navigator: Ons.NavigatorCtrl<Page>,
    importedVisitId: ImportedVisitId,
    patientName: string
  ) {
    return (
      <ConvertImportedVisitActivity
        api={this.props.api}
        importedVisitId={importedVisitId}
        patientName={patientName}
        onBackClick={() => navigator.popPage()}
      />
    );
  }

  renderPatientTaskInstanceScreen(
    navigator: Ons.NavigatorCtrl<Page>,
    patientTaskInstanceId: PatientTaskInstanceId,
    initialScheduleDateTimePickerOpen: boolean
  ) {
    return (
      <PatientTaskInstanceActivity
        api={this.props.api}
        appState={this.props.appState}
        patientTaskInstanceId={patientTaskInstanceId}
        initialScheduleDateTimePickerOpen={initialScheduleDateTimePickerOpen}
        caregiverGeoLocation={
          isLoading(this.props.appState.appCaregiverState)
            ? loading()
            : this.props.appState.appCaregiverState.caregiver.addressGeoLocation
        }
        onRequestNavigate={(page) => navigator.pushPage(page)}
        onClickNewChatWithMedflyt={this.handleClickNewChat}
        onClickGoBack={() => navigator.popPage()}
      />
    );
  }

  renderPatientTaskInstanceChangeScreen(
    navigator: Ons.NavigatorCtrl<Page>,
    patientTaskInstanceId: PatientTaskInstanceId
  ) {
    return (
      <PatientTaskInstanceChangeActivity
        api={this.props.api}
        appState={this.props.appState}
        patientTaskInstanceId={patientTaskInstanceId}
        onRequestNavigate={(page) => navigator.pushPage(page)}
        onClickChatWithAgency={this.handleClickAssignedTask}
        onClickGoBack={() => navigator.popPage()}
      />
    );
  }

  renderPatientScreen(navigator: Ons.NavigatorCtrl<Page>, patientId: PatientId) {
    return (
      <PatientActivity
        api={this.props.api}
        appState={this.props.appState}
        patientId={patientId}
        onRequestNavigate={(page) => navigator.pushPage(page)}
        onClickChat={this.handleClickChatWithAgency}
        onClickGoBack={() => navigator.popPage()}
        onClock={this.handleClock}
      />
    );
  }

  renderPatientVisitsAndTasksScreen(
    navigator: Ons.NavigatorCtrl<Page>,
    patientId: PatientId,
    listType: "Visits" | "Tasks"
  ) {
    return (
      <PatientVisitsAndTasksActivity
        api={this.props.api}
        appState={this.props.appState}
        patientId={patientId}
        listType={listType}
        onRequestNavigate={(page) => navigator.pushPage(page)}
        onClickNewChatWithMedflyt={this.handleClickNewChat}
        onClock={this.handleClock}
        onClickGoBack={() => navigator.popPage()}
      />
    );
  }

  renderPatientTaskInstanceDocumentActivity(
    navigator: Ons.NavigatorCtrl<Page>,
    patientId: PatientId,
    patientTaskInstanceId: PatientTaskInstanceId,
    patientDocumentTypeId: PatientDocumentTypeId
  ) {
    return (
      <PatientTaskInstanceDocumentActivity
        api={this.props.api}
        appState={this.props.appState}
        patientId={patientId}
        patientTaskInstanceId={patientTaskInstanceId}
        patientDocumentTypeId={patientDocumentTypeId}
        onClickGoBack={() => navigator.popPage()}
        onClickAddPhysician={() => navigator.pushPage({ type: "AddPatientPhysician", patientId })}
      />
    );
  }

  renderMyVirtualId() {
    return <VirtualIdCardActivity onClickGoBack={this.handleClickGoBack} />;
  }

  renderDocViewerScreen(navigator: Ons.NavigatorCtrl<Page>, title: string, pdfUrl: string) {
    return (
      <UploadedFileViewerScreen
        onBackClick={() => navigator.popPage()}
        url={pdfUrl}
        title={title}
      />
    );
  }

  renderCaregiverPaystubListActivity(caregiver: AppCaregiver | Loading) {
    return (
      <PaystubListActivity
        caregiver={caregiver}
        api={this.props.api}
        onClickGoBack={this.handleClickGoBack}
        onRequestNavigate={this.handleRequestNavigate}
      />
    );
  }

  renderCaregiverPaystubDetailsActivity(paystub: Paystub) {
    return (
      <PaystubDetailsActivity
        api={this.props.api}
        paystub={paystub}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderTrainingCenterActivity(page: Extract<Page, { type: "TrainingCenter" }>) {
    return (
      <TrainingCenterActivity
        api={this.props.api}
        appState={this.props.appState}
        languagePreference={this.state.languagePreference}
        initialTab={page.initialTab}
        onClickGoBack={this.handleClickGoBack}
        onRequestNavigate={this.handleRequestNavigate}
      />
    );
  }

  renderOtherAgenciesChat = () => {
    return (
      <OtherAgenciesChatActivity
        appState={this.props.appState}
        chatSystemStrategies={this.props.chatSystemStrategies}
        onClickGoBack={this.handleClickGoBack}
        onRequestNavigate={this.handleRequestNavigate}
      />
    );
  };

  renderCareMomentsExplainedActivity() {
    return <CareMomentsExplainedActivity onClickGoBack={this.handleClickGoBack} />;
  }

  renderCareMomentsIntroActivity() {
    return (
      <CareMomentsIntroActivity
        onRequestNavigate={this.handleRequestNavigate}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderCareMomentsActivity() {
    return (
      <CareMomentsActivity
        api={this.props.api}
        appState={this.props.appState}
        onRequestNavigate={this.handleRequestNavigate}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderRaffleActivity() {
    return (
      <RaffleActivity
        api={this.props.api}
        appState={this.props.appState}
        onRequestNavigate={this.handleRequestNavigate}
        onClickGoBack={this.handleClickGoBack}
      />
    );
  }

  renderRaffleInfoActivity() {
    return <RaffleInfoActivity onClickGoBack={this.handleClickGoBack} />;
  }

  renderPatientScheduleActivity(patientTaskInstanceId: PatientTaskInstanceId) {
    return (
      <PatientScheduleActivity
        api={this.props.api}
        appState={this.props.appState}
        onClickGoBack={this.handleClickGoBack}
        patientTaskInstanceId={patientTaskInstanceId}
      />
    );
  }

  renderAddPatientPhysicianActivity(patientId: PatientId) {
    return (
      <AddPhysicianActivity
        api={this.props.api}
        onClickGoBack={this.handleClickGoBack}
        patientId={patientId}
      />
    );
  }

  renderPage = (route: Page, navigator: Ons.NavigatorCtrl<Page>): JSX.Element => {
    let page: JSX.Element;

    this.navigator = navigator;

    switch (route.type) {
      case "Main":
        page = this.renderMainActivity({
          defaultNavigation: route.defaultNavigation,
          scrollToScheduleDate: route.scrollToScheduleDate,
        });
        break;
      case "Profile":
        page = this.renderProfileActivity(navigator);
        break;
      case "Settings":
        page = this.renderSettingsActivity(navigator);
        break;
      case "SettingsSocial":
        page = this.renderSettingsSocialActivity(navigator);
        break;
      case "SettingsHelp":
        page = this.renderSettingsHelpActivity(navigator);
        break;
      case "SettingsGeneral":
        page = this.renderSettingsGeneralActivity(navigator);
        break;
      case "SettingsNotifications":
        page = this.renderSettingsNotificationsActivity(navigator);
        break;
      case "TrainingCenterBundle":
        page = this.renderTrainingCenterBundleActivity(navigator, route.bundleDueDateId);
        break;
      case "TrainingCenterBundleItem":
        page = this.renderTrainingCenterBundleItemActivity(
          navigator,
          route.bundleDueDateId,
          route.bundleItemId
        );
        break;
      case "MedflytPassport":
        page = this.renderMedflytPassportScreen(navigator);
        break;
      case "MedflytPassportCategory":
        page = this.renderMedflytPassportCategoryScreen(navigator, route.category, route.language);
        break;
      case "MedflytPassportDocuments":
        page = this.renderMedflytPassportDocumentsScreen(
          navigator,
          route.agencyId,
          route.documentType,
          route.language
        );
        break;
      case "UploadedFileViewer":
        page = this.renderUploadedFileActivity(navigator, route.url);
        break;
      case "Compliance":
        page = this.renderComplianceScreen(navigator);
        break;
      case "MyAgencies":
        page = this.renderMyAgenciesActivity(navigator, route.addAgency, route.resetOnFinish);
        break;
      case "MedFlytChallenge":
        page = this.renderMedFlytChallengeActivity(navigator);
        break;
      case "AvailableVisit":
        page = this.renderAvailableVisitActivity(route.visitBroadcastId);
        break;
      case "OnboardingVisit":
        page = this.renderOnboardingVisitActivity(route.onboardingVisitId);
        break;
      case "VisitInstance":
        page = this.renderVisitInstanceActivity(route.visitInstanceId);
        break;
      case "Notifications":
        page = this.renderNotificationActivity(navigator);
        break;
      case "Chat":
        page = this.renderChatActivity(
          navigator,
          route.chatRoomId,
          route.linkToVisit,
          route.initialMessage
        );
        break;
      case "ReferralCode":
        page = this.renderReferralCodeActivity(navigator);
        break;
      case "CaseSettings":
        page = this.renderCaseSettings(navigator);
        break;
      case "MedflytCaseSettings":
        page = this.renderCaseSettingsV2(navigator, route.showMyStafingPreferences);
        break;
      case "MyStaffingPreferences":
        page = this.renderMyStaffingPreferences(navigator);
        break;
      case "RescheduleRequest":
        page = this.renderRescheduleRequest(navigator, route.scheduledVisitDetails, route.isCancel);
        break;
      case "EditRequests":
        page = this.renderEditRequests(navigator);
        break;
      case "AddChats":
        page = this.renderAddChat(navigator);
        break;
      case "ScheduledVisitDocScreen":
        page = this.renderScheduledVisitDocScreen(
          navigator,
          route.requiredDocument,
          route.patientId,
          route.updateDocuments
        );
        break;
      case "VisitPlanOfCare":
        page = this.renderVisitPlanOfCareScreen(
          navigator,
          route.visitInstanceId,
          route.init,
          route.updatePlanOfCare
        );
        break;
      case "TaskPlanOfCare":
        page = this.renderTaskPlanOfCareScreen(
          navigator,
          route.taskInstanceId,
          route.init,
          route.updatePlanOfCare
        );
        break;
      case "RecruitmentMainPage":
        page = this.renderRecruitementMainScreen(navigator);
        break;
      case "RecruitmentFormPage":
        page = this.renderRecruitementFormScreen(navigator);
        break;
      case "VisitReschedule":
        page = this.renderRescheduleScreen(
          navigator,
          route.params,
          route.patientPhoneNumber,
          route.onChosenDate
        );
        break;
      case "CovidPage":
        page = this.renderCovidPage(navigator, route.coronaReports);
        break;
      case "ConvertImportedVisit":
        page = this.renderConvertImportedVisitScreen(
          navigator,
          route.importedVisitId,
          route.patientName
        );
        break;
      case "PatientTaskInstance":
        page = this.renderPatientTaskInstanceScreen(
          navigator,
          route.patientTaskInstanceId,
          route.initialScheduleDateTimePickerOpen
        );
        break;
      case "PatientTaskInstanceChange":
        page = this.renderPatientTaskInstanceChangeScreen(navigator, route.patientTaskInstanceId);
        break;
      case "Patient":
        page = this.renderPatientScreen(navigator, route.patientId);
        break;
      case "PatientVisitsAndTasks":
        page = this.renderPatientVisitsAndTasksScreen(navigator, route.patientId, route.listType);
        break;
      case "PatientTaskInstanceDocument":
        page = this.renderPatientTaskInstanceDocumentActivity(
          navigator,
          route.patientId,
          route.patientTaskInstanceId,
          route.patientDocumentId
        );
        break;
      case "DocViewerScreen":
        page = this.renderDocViewerScreen(navigator, route.title, route.pdfUrl);
        break;
      case "AvailableTaskScreen":
        page = this.renderAvailableTaskActivity(route.taskBroadcastId);
        break;
      case "VisitPendingConfirmation":
        page = this.renderVisitPendingConfirmation(route.visitId, route.isReviewOnly);
        break;
      case "TimesheetActivity":
        page = this.renderTimesheetActivity(route.visitInstanceId);
        break;
      case "OnboardingActivity":
        page = this.renderOnboardingActivity(navigator, null);
        break;
      case "OnboardingSectionActivity":
        page = this.renderOnboardingSectionActivity(
          route.sections,
          route.section,
          route.caregiverApplicationQuery,
          route.language,
          route.customDirectionParams
        );
        break;
      case "MyMedflytCompliance":
        page = this.renderMyMedflytComplianceActivity();
        break;
      case "ComplianceItem":
        page = this.renderComplianceItemActivity(route.documentTypeId);
        break;
      case "ComplianceDocument":
        page = this.renderComplianceDocumentActivity(route.document);
        break;
      case "MyVirtualId":
        page = this.renderMyVirtualId();
        break;
      case "ChatRoom":
        page = this.renderChatRoomActivity(route.ticketId);
        break;
      case "ComplianceDocumentForm":
        page = this.renderComplianceDocumentForm(route.documentTypeId);
        break;
      case "ComplianceI9":
        page = this.renderComplianceI9();
        break;
      case "InstaPay":
        page = this.renderInstaPayActivity();
        break;
      case "InstaPayHistory":
        page = this.renderInstaPayHistoryActivity();
        break;
      case "SelectInstaPayVisits":
        page = this.renderSelectInstaPayVisitsActivity(route.weekNum);
        break;
      case "FillBankInformation":
        page = this.renderFillBankInformationActivity(route);
        break;
      case "EditBankInformation":
        page = this.renderEditBankInformationActivity();
        break;
      case "OnboardingActivityWithDirection":
        page = this.renderOnboardingActivity(navigator, route.customDirectionParams);
        break;
      case "InformationCenter":
        page = this.renderInformationCenterActivity(route.caregiver);
        break;
      case "ImportantInformation":
        page = this.renderImportantInformationActivity();
        break;
      case "DocumentDownload":
        page = this.renderDocumentDownloadActivity(route.documentType);
        break;
      case "CarebackPoints":
        analyticsUtils.track("Careback screen entered");
        page = this.renderCarebackPointsActivity(route.caregiver);
        break;
      case "CaregiverPaystubList":
        page = this.renderCaregiverPaystubListActivity(route.caregiver);
        break;
      case "CaregiverPaystubDetails":
        page = this.renderCaregiverPaystubDetailsActivity(route.paystub);
        break;
      case "TrainingCenter":
        page = this.renderTrainingCenterActivity(route);
        break;
      case "OtherAgenciesChat":
        page = this.renderOtherAgenciesChat();
        break;
      case "CompliantItems":
        page = this.renderCompliantItemsActivity(route);
        break;
      case "CareMomentsExplained":
        page = this.renderCareMomentsExplainedActivity();
        break;
      case "CareMomentsIntro":
        page = this.renderCareMomentsIntroActivity();
        break;
      case "CareMoments":
        page = this.renderCareMomentsActivity();
        break;
      case "Raffle":
        page = this.renderRaffleActivity();
        break;
      case "RaffleInfo":
        page = this.renderRaffleInfoActivity();
        break;
      case "PatientSchedule":
        page = this.renderPatientScheduleActivity(route.patientTaskInstanceId);
        break;
      case "AddPatientPhysician":
        page = this.renderAddPatientPhysicianActivity(route.patientId);
        break;
      default:
        return assertNever(route);
    }

    const key = (() => {
      // TODO: use pageKey instead of addAgency.
      if (route.type === "MyAgencies") {
        return route.type + route.addAgency;
      }

      if (route.type === "ChatRoom") {
        return route.type + route.ticketId;
      }

      if ("pageKey" in route) {
        return route.type + route.pageKey;
      }

      return route.type;
    })();

    return (
      <Ons.Page key={"page-key-" + key} data-page-key={key}>
        {page}
      </Ons.Page>
    );
  };

  updatePopoverCounter(popover: PromoPopover, dontShowAgain?: boolean) {
    getLocalStorageItem(popover.localStorageKey).then((amountShown) => {
      let updatedValue = amountShown === null ? "1" : "" + (+amountShown + 1);

      if (dontShowAgain === true) {
        updatedValue = "999";
      }

      setLocalStorageItem(popover.localStorageKey, updatedValue);
    });
  }

  handleFcmNotificationStatusUpdate = (enabled: boolean) => {
    if (
      !isLoading(this.props.appState.appCaregiverState) &&
      this.props.appState.appCaregiverState.caregiver.isCaregiverOfMedFlyt
    ) {
      this.props.api.updatePushNotificationStatus({
        enabled: enabled,
      });
    }
  };

  render() {
    console.log("AppNavigator render");

    const defaultRouteStack: Page[] = [
      {
        type: "Main",
      },
    ];

    const initialRouteStack: Page[] = defaultRouteStack;

    return (
      <ErrorBoundary>
        <ToastsContainer />
        <APINotProductionAlert apiConfig={this.props.api.apiConfig} />
        <If condition={this.props.shouldShowPromotionalPopover} comment="Show promotional popover">
          {isLoading(this.state.showPromotionalPopover) ||
          this.state.showPromotionalPopover === null ? null : (
            <PromoPopoverScreen
              popover={this.state.showPromotionalPopover}
              api={this.props.api}
              onClickClose={() => {
                const popover = this.state.showPromotionalPopover;
                if (popover !== null && !isLoading(popover)) {
                  this.updatePopoverCounter(popover);
                }
                this.setState({ showPromotionalPopover: null });
              }}
              onClickPassport={(dontShowAgain) => {
                this.setState({ showPromotionalPopover: null });
                const popover = this.state.showPromotionalPopover;
                if (popover !== null && !isLoading(popover)) {
                  this.updatePopoverCounter(popover, dontShowAgain);
                }

                if (this.navigator !== null) {
                  this.navigator.pushPage({ type: "MedflytPassport" });
                }
              }}
              onClickCaregiverSurvey={() => {
                this.setState({ showPromotionalPopover: null });
                const popover = this.state.showPromotionalPopover;
                if (popover !== null && !isLoading(popover)) {
                  this.updatePopoverCounter(popover);
                }
              }}
            />
          )}
        </If>
        <AppRating />
        <AppNavigatorNavigator
          renderPage={this.renderPage}
          initialRouteStack={initialRouteStack}
          animation="lift"
        />
        <InAppNotifications
          api={this.props.api}
          onClickPushNotification={this.handleFcmNotificationClick}
          onUpdatePushNotificationStatus={this.handleFcmNotificationStatusUpdate}
        />
        <LogRocketAnalytics appState={this.props.appState} />
        <AppUrlListener onRequestNavigate={this.handleRequestNavigate} />
        <StoreRedirect />
      </ErrorBoundary>
    );
  }
}

const AppNavigatorNavigator = Ons.Navigator;

function lookupPatientChatRoom(
  appChatState: AppChatState,
  patientId: PatientId
): ChatRoomId | null {
  if (isLoading(appChatState.chatRooms)) {
    return null;
  }

  for (const chatRoom of appChatState.chatRooms.values()) {
    if (chatRoom.chatRoomType.type === "CaregiverPatientChat") {
      if (chatRoom.chatRoomType.patientId === patientId) {
        return chatRoom.id;
      }
    }
  }

  return null;
}

function lookupAgencyMemberChatRoom(
  appChatState: AppChatState,
  agencyMemberId: AgencyMemberId
): ChatRoom | null {
  if (isLoading(appChatState.chatRooms)) {
    return null;
  }

  for (const chatRoom of appChatState.chatRooms.values()) {
    if (chatRoom.chatRoomType.type === "AgencyMemberCaregiverChat") {
      if (chatRoom.chatRoomType.agencyMemberId === agencyMemberId) {
        return chatRoom;
      }
    }
  }

  return null;
}

async function getPatientChatRoomId(
  api: API,
  chatState: ChatState,
  patientId: PatientId
): Promise<ChatRoomId | null> {
  const chatRoomId = lookupPatientChatRoom(chatState.getAppChatState(), patientId);

  if (chatRoomId !== null) {
    return chatRoomId;
  }

  const newChatRoom = await retryableModalNetworkRequest(
    () => chatState.createChatRoom(() => api.getPatientChatRoom(patientId)),
    true
  );

  return fmap(newChatRoom, (x) => x.id);
}

async function getAgencyMemberChatRoomId(
  api: API,
  chatState: ChatState,
  agencyMemberId: AgencyMemberId
): Promise<ChatRoomId | null> {
  const chatRoom = await getAgencyMemberChatRoom(api, chatState, agencyMemberId);

  return chatRoom !== null ? chatRoom.id : null;
}

async function getAgencyMemberChatRoom(
  api: API,
  chatState: ChatState,
  agencyMemberId: AgencyMemberId
): Promise<ChatRoom | null> {
  const chatRoomId = lookupAgencyMemberChatRoom(chatState.getAppChatState(), agencyMemberId);

  if (chatRoomId !== null) {
    return chatRoomId;
  }

  const newChatRoom = await retryableModalNetworkRequest(
    () => chatState.createChatRoom(() => api.getAgencyMemberChatRoom(agencyMemberId)),
    true
  );

  if (newChatRoom === null) {
    return null;
  }

  return newChatRoom;
}

function trainingCenterFindAllLanguages(
  bundles: List<TrainingCenterCaregiverBundle>
): HashSet<TrainingCenterDisplayLanguage> {
  let result = HashSet.empty<TrainingCenterDisplayLanguage>();

  for (const bundle of bundles) {
    result = result.union(extractLanguages(bundle.bundleDisplayData));

    for (const item of bundle.bundleItems) {
      result = result.union(extractLanguages(item.displayData));

      switch (item.media.type) {
        case "PDF":
          break;
        case "Video":
          result = result.union(extractLanguages(item.media.videoInfo));
          result = result.union(extractLanguages(item.media.videoWatchData));

          // Note: we intentionally do not extract languages from
          // the video subtitles
          break;
        case "TestOnly":
          break;
        default:
          assertNever(item.media);
      }

      for (const question of item.currentTestQuestions) {
        result = result.union(extractLanguages(question.displayData));
      }
    }
  }

  return result;
}

function extractLanguages<T>(
  translations: List<TrainingCenterTranslated<T>>
): HashSet<TrainingCenterDisplayLanguage> {
  return HashSet.fromCollection(translations.map((x) => x.language));
}

export interface ReceivedFcm {
  tap: "foreground" | "background" | null;
  message: string | null;
  data: FcmPushNotification | null;
}
